import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#0B0B10] text-white">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-fuchsia-600/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-violet-600/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-violet-400" />
            </div>
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-zinc-400">Last updated: February 2025</p>
        </motion.div>

        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="prose prose-invert prose-zinc max-w-none"
        >
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-zinc-400 leading-relaxed">
              By accessing or using Private After Dark ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Age Requirement</h2>
            <p className="text-zinc-400 leading-relaxed">
              You must be at least 18 years of age to use this Service. By using the Service, you represent and warrant 
              that you are at least 18 years old. We reserve the right to terminate your access if we discover you are under 18.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. Nature of the Service</h2>
            <p className="text-zinc-400 leading-relaxed">
              Private After Dark is an AI-powered fantasy companion service. All characters are artificial intelligence 
              personas and do not represent real individuals. Interactions are for entertainment purposes only.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. User Conduct</h2>
            <p className="text-zinc-400 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Share access credentials with others</li>
              <li>Attempt to exploit or hack the Service</li>
              <li>Share content from the Service publicly without permission</li>
              <li>Use the Service if you are under 18</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Subscription and Payments</h2>
            <p className="text-zinc-400 leading-relaxed">
              Premium subscriptions are billed monthly. You may cancel at any time. Refunds are provided at our discretion. 
              By subscribing, you authorize recurring charges to your payment method.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Content Disclaimer</h2>
            <p className="text-zinc-400 leading-relaxed">
              The Service contains adult-oriented content. All AI-generated responses are for fantasy and entertainment 
              purposes. The Service does not promote or condone any illegal activities.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Limitation of Liability</h2>
            <p className="text-zinc-400 leading-relaxed">
              The Service is provided "as is" without warranties. We are not liable for any damages arising from your 
              use of the Service. Use at your own risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Changes to Terms</h2>
            <p className="text-zinc-400 leading-relaxed">
              We reserve the right to modify these terms at any time. Continued use after changes constitutes acceptance 
              of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Contact</h2>
            <p className="text-zinc-400 leading-relaxed">
              For questions about these Terms, please contact us through our Telegram bot.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
